import { useEffect, useState } from 'react';
import { CheckIcon, TrashIcon, PlusIcon, ThemedSelect } from '@bizfirst/common-react';
import { useTrustedDidAdminApiClient } from '../../context/AiAgentAdminApiContext';
import type { TrustDirection, TrustLevel, TrustedDIDDTO } from '../../api/trustedDidAdminApiClient';
import { ConfirmDialog } from '../Shared/ConfirmDialog';

const TRUST_LEVELS: TrustLevel[] = ['full', 'partial', 'conditional'];

/**
 * Manage a node's trusted-DID allow-lists (Caller + Sender) inline, from the node's own config
 * screen. Public keys/DIDs only — no private material ever appears here. Direction filters the
 * same Did_TrustedDIDs rows into the two roles DIDCommCapability.AllowedCallers/AllowedSenders
 * previously modeled as JSON.
 */
export function TrustedDIDManager({ nodeId }: { nodeId: number }) {
  const api = useTrustedDidAdminApiClient();
  const [entries, setEntries] = useState<TrustedDIDDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newDid, setNewDid] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [newDirection, setNewDirection] = useState<TrustDirection>('Caller');
  const [newTrustLevel, setNewTrustLevel] = useState<TrustLevel>('full');
  const [adding, setAdding] = useState(false);

  const [pendingRevoke, setPendingRevoke] = useState<TrustedDIDDTO | null>(null);
  const [revoking, setRevoking] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    const result = await api.getByNodeId(nodeId);
    if (!result.success) setError(result.error?.message ?? 'Failed to load trusted DIDs.');
    setEntries(result.data ?? []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeId]);

  const handleAdd = async () => {
    if (!newDid.trim()) return;
    setAdding(true);
    setError(null);
    const result = await api.trust({
      NodeID: nodeId,
      DID: newDid.trim(),
      DisplayName: newDisplayName.trim() || undefined,
      TrustLevel: newTrustLevel,
      Direction: newDirection,
    });
    setAdding(false);
    if (result.success) {
      setNewDid('');
      setNewDisplayName('');
      await load();
    } else {
      setError(result.error?.message ?? 'Failed to add trusted DID (must match did:method:specific-id format).');
    }
  };

  const handleVerify = async (entry: TrustedDIDDTO) => {
    const result = await api.verify(entry.TrustedDIDID);
    if (result.success) await load();
  };

  const handleTrustLevelChange = async (entry: TrustedDIDDTO, level: TrustLevel) => {
    const result = await api.updateTrustLevel(entry.TrustedDIDID, level);
    if (result.success) await load();
  };

  const handleRevokeConfirmed = async () => {
    if (!pendingRevoke) return;
    setRevoking(true);
    const result = await api.revoke(pendingRevoke.TrustedDIDID);
    setRevoking(false);
    if (result.success) {
      setPendingRevoke(null);
      setEntries((prev) => prev.filter((e) => e.TrustedDIDID !== pendingRevoke.TrustedDIDID));
    }
  };

  const callers = entries.filter((e) => e.Direction === 'Caller');
  const senders = entries.filter((e) => e.Direction === 'Sender');

  const renderTable = (rows: TrustedDIDDTO[], emptyLabel: string) => (
    <>
      {rows.length === 0 && <div className="aaa-entity-dropdown-empty">{emptyLabel}</div>}
      {rows.length > 0 && (
        <table className="aaa-table" style={{ marginBottom: 16 }}>
          <thead>
            <tr>
              <th>DID</th>
              <th>Display Name</th>
              <th>Trust Level</th>
              <th>Verified</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((entry) => (
              <tr key={entry.TrustedDIDID}>
                <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{entry.DID}</td>
                <td>{entry.DisplayName || ''}</td>
                <td>
                  <ThemedSelect
                    value={entry.TrustLevel}
                    onChange={(v) => handleTrustLevelChange(entry, v as TrustLevel)}
                    options={TRUST_LEVELS.map((l) => ({ value: l, label: l }))}
                    ariaLabel="Trust level"
                  />
                </td>
                <td>
                  {entry.VerifiedOn ? (
                    <span className="aaa-badge aaa-badge-active">Verified</span>
                  ) : (
                    <button type="button" className="aaa-icon-btn" onClick={() => handleVerify(entry)} title="Mark verified">
                      <CheckIcon size={13} /> Verify
                    </button>
                  )}
                </td>
                <td>
                  <button type="button" className="aaa-icon-btn aaa-icon-btn-danger" onClick={() => setPendingRevoke(entry)} title="Revoke trust">
                    <TrashIcon size={13} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );

  return (
    <div>
      {error && (
        <div className="error-message" style={{ color: '#ef4444', fontSize: 13, marginBottom: 8, padding: 8, backgroundColor: 'rgba(239, 68, 68, 0.1)', borderRadius: 4 }}>
          {error}
        </div>
      )}

      <div className="aaa-form-grid" style={{ marginBottom: 16 }}>
        <div className="aaa-form-group aaa-form-group-full">
          <label>DID</label>
          <input type="text" value={newDid} onChange={(e) => setNewDid(e.target.value)} placeholder="did:method:specific-id" style={{ fontFamily: 'monospace' }} />
        </div>
        <div className="aaa-form-group">
          <label>Display Name</label>
          <input type="text" value={newDisplayName} onChange={(e) => setNewDisplayName(e.target.value)} placeholder="Optional" />
        </div>
        <div className="aaa-form-group">
          <label>Direction</label>
          <ThemedSelect
            value={newDirection}
            onChange={(v) => setNewDirection(v as TrustDirection)}
            options={[
              { value: 'Caller', label: 'Caller (who may invoke this node)' },
              { value: 'Sender', label: 'Sender (whose signed messages are accepted)' },
            ]}
            ariaLabel="Direction"
          />
        </div>
        <div className="aaa-form-group">
          <label>Trust Level</label>
          <ThemedSelect
            value={newTrustLevel}
            onChange={(v) => setNewTrustLevel(v as TrustLevel)}
            options={TRUST_LEVELS.map((l) => ({ value: l, label: l }))}
            ariaLabel="Trust level"
          />
        </div>
        <div className="aaa-form-group" style={{ alignSelf: 'end' }}>
          <button type="button" className="aaa-btn aaa-btn-primary" onClick={handleAdd} disabled={!newDid.trim() || adding}>
            <PlusIcon size={13} /> {adding ? 'Adding…' : 'Add'}
          </button>
        </div>
      </div>

      {loading && <div className="aaa-editor-loading">Loading…</div>}

      {!loading && (
        <>
          <h4 style={{ margin: '12px 0 8px' }}>Allowed Callers</h4>
          {renderTable(callers, 'No allowed callers — this node will reject every caller (fail-closed).')}

          <h4 style={{ margin: '12px 0 8px' }}>Allowed Senders</h4>
          {renderTable(senders, 'No allowed senders — signature verification against a sender allow-list will reject everyone until one is added. Note: this list does not gate decryption (the multi-recipient envelope scheme has no sender identity).')}
        </>
      )}

      <ConfirmDialog
        isOpen={!!pendingRevoke}
        title="Revoke Trust"
        message={`Revoke trust for "${pendingRevoke?.DisplayName || pendingRevoke?.DID}"? It will no longer be treated as a trusted ${pendingRevoke?.Direction === 'Sender' ? 'sender' : 'caller'}.`}
        busy={revoking}
        onConfirm={handleRevokeConfirmed}
        onCancel={() => setPendingRevoke(null)}
      />
    </div>
  );
}
